import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import makeStyles from '@material-ui/core/styles/makeStyles';
import classnames from 'classnames';
import _cloneDeep from 'lodash/cloneDeep';
import _sortBy from 'lodash/sortBy';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import config from '../../app/config';
import { color } from '../../app/theme';
import { NextResourceName } from '../../app/types';
import { validateWaLit } from '../../app/utils';
import IconAddCircle from '../../assets/images/Icons/IconAddCircle';
import icHelpCircle from '../../assets/images/svg/ic-help-circle.svg';
import { setDefaultTemplate, setState } from '../../features/mainSlice';
import {
  checkResourceLimit,
  saveDefaultTemplate,
} from '../../features/organizationSlice';
import Button from './Button';
import DatePicker from './DatePicker';
import DialogText from './DialogText';
import ItemLabel from './ItemLabel';
import Tabs from './Tabs';
import TextAreaCard from './TextAreaCard';
import TextFieldCardCounter from './TextFieldCardCounter';
import TimePicker from './TimePicker';
import Tip from './Tip';
import Tooltip from './Tooltip';
import WhatsAppListOption from './WhatsAppListOption';

const useStyles = makeStyles(
  theme => ({
    root: {
      margin: 'auto',
      width: 560,
      backgroundColor: color.$7,
      borderRadius: 12,
      boxShadow:
        '0 16px 48px 0 rgba(0, 0, 0, 0.07), 0 6px 18px 0 rgba(0, 0, 0, 0.03)',
    },
    contentCardContainer: {
      marginTop: '36px',
      padding: '16px 12px',
    },
    contentContainer: {
      minHeight: 174,
      padding: '0 24px 24px',
      overflow: 'auto',
    },
    icon: {
      display: 'inline-flex',
      alignItems: 'center',
      color: color.$2,
      fontSize: 15,
      lineHeight: 1,
      cursor: 'pointer',
      '&:hover': {
        opacity: '0.6',
      },
      '& > svg': {
        marginRight: 4,
      },
    },
    selectTemplateBlock: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: 24,
    },
    selectTemplate: {
      ...theme.link.primaryGreen.default,
      margin: '0 0 0 4px',
    },
    actionContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'end',
    },
    date: {
      width: 180,
    },
    time: {
      width: 100,
      marginLeft: 8,
    },
    saveTemplateFormControlLabel: {
      flex: '0 0 auto',
    },
    saveTemplateLabel: {
      fontSize: 15,
      lineHeight: 1,
      color: color.$13,
    },
    saveTemplateName: {
      width: 265,
    },
    opertationBlock: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
    templateTitle: {
      marginRight: 12,
      fontSize: 15,
      color: color.$3,
      display: 'flex',
      alignItems: 'center',
    },
    expiredAt: {
      marginRight: 0,
    },
    note: {
      width: 442,
    },
    noteLong: {
      width: 472,
    },
    expiredAtRow: {
      display: 'flex',
      alignItems: 'center',
      height: 40,
    },
    actionBar: {
      width: '100%',
    },
    actionBarNoSave: {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
    },
    tabs: {
      backgroundColor: 'transparent',
      padding: 0,
    },
    red: {
      color: color.$17,
    },
    tabListRoot: {
      display: 'flex',
      height: '491px',
    },
    tabListLeft: {
      width: '216px',
      margin: '24px 12px 0 12px',
    },
    tabListRight: {
      width: '648px',
      marginLeft: '24px',
    },
    tabListCount: {
      marginTop: '12px',
      display: 'flex',
      justifyContent: 'right',
      fontFamily: 'PingFang TC',
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '18px',
      color: color.$12,
    },
    tabListTitle: {
      marginTop: '12px',
      fontFamily: 'PingFang TC',
      fontSize: '17px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '17px',
      color: color.$13,
    },
  }),
  {
    name: 'WhatsAppListCardDialog',
  },
);

function WhatsAppListCardDialog({
  templateExpiredAt,
  templateId,
  name,
  data,
  onCancel,
  onSubmit,
  messageIndex,
  disableTemplate,
  disabled,
  isSendLoading,
  isMessageCenter,
  isShowSubmit,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isSaveAsDefaultTemplate = useSelector(
    store => store.ma.isSaveAsDefaultTemplate,
  );

  const { objectId: orgId } = useSelector(store => store.organization);
  const [templateName, setTemplateName] = useState(
    name ||
      `${t('types.message-template-type.waList')} ${moment().format(
        'YYYY-MM-DD HH:mm',
      )}`,
  );
  const [isModified, setIsModified] = useState(false);
  const [localTitle, setLocalTitle] = useState(data.title);
  const [localSubtitle, setLocalSubtitle] = useState(data.subtitle);
  const [localDesc, setLocalDesc] = useState(data.desc);
  const [localListButtonText, setLocalListButtonText] = useState(
    data.listButtonText,
  );
  const [isShowMsgTemplate, setIsShowMsgTemplate] = useState(false);
  const [localSectionIndex, setLocalSectionIndex] = useState(0);
  const [localSections, setLocalSections] = useState(
    data.sections.length ? data.sections : [t('waList.defaultSectionName')],
  );
  const [localSectionTitle, setLocalSectionTitle] = useState(localSections[0]);
  const [localSectionOptions, setLocalSectionOptions] = useState(() => {
    return data.buttons.map((button, index) => ({
      index,
      name: button.title || '',
      desc: button.subtitle || '',
      sectionIndex: button.sectionIndex,
    }));
  });
  const [localOptions, setLocalOptions] = useState(
    localSectionOptions.filter(
      option => option.sectionIndex === localSectionIndex,
    ),
  );
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [expiredAt, setExpiredAt] = useState(templateExpiredAt);
  const [tabIndex, setTabIndex] = useState(0);
  const tabs = [
    {
      key: 'card',
      name: t('waList.tabCard'),
    },
    {
      key: 'list',
      name: t('waList.tabList'),
    },
  ];
  const selectedTab = useMemo(() => {
    return tabs[tabIndex || 0];
  }, [tabIndex, tabs]);
  const optionLimit = 10;
  const isOverLimit = useMemo(() => {
    return localSectionOptions.length >= optionLimit;
  }, [localSectionOptions]);
  const isDeleteable = useMemo(() => {
    return localSections.length > 1;
  }, [localSections]);
  const isEditable = true;
  useEffect(() => {
    setLocalOptions(
      localSectionOptions.filter(
        option => option.sectionIndex === localSectionIndex,
      ),
    );
  }, [localSectionOptions, localSectionIndex]);

  useEffect(() => {
    const getResource = async () => {
      const isShow = await dispatch(
        checkResourceLimit(NextResourceName.MSG_TEMPLATE_LIMIT),
      );
      setIsShowMsgTemplate(isShow);
    };
    getResource();
  }, []);

  const getOptionCount = index => {
    return localSectionOptions.filter(option => option.sectionIndex === index)
      .length;
  };

  const onTabIndexChange = (event, index) => {
    setTabIndex(index);
  };

  const handleAddSection = () => {
    setLocalSections([...localSections, t('waList.defaultSectionName')]);
    setLocalSectionOptions([
      ...localSectionOptions,
      {
        index: localSectionOptions.length,
        name: '',
        desc: '',
        sectionIndex: localSections.length,
      },
    ]);
  };

  const handleSectionClick = index => {
    const title = localSections[index];
    setLocalSectionTitle(title);
    setLocalSectionIndex(index);
  };

  const handleSectionEdit = (index, value) => {
    setLocalSections(
      [...localSections].map((l, i) => {
        if (i === index) {
          return value;
        }
        return l;
      }),
    );
    setLocalSectionTitle(value);
  };

  const handleSectionDelete = index => {
    setLocalSections(
      localSections.filter((section, sIndex) => sIndex !== index),
    );
    setLocalSectionOptions(
      localSectionOptions
        .filter(option => option.sectionIndex !== index)
        .map((option, oIndex) => {
          const o = option;
          o.index = oIndex;
          if (o.sectionIndex > index) {
            o.sectionIndex -= 1;
          }
          return o;
        }),
    );
    let newIndex;
    if (index === localSectionIndex) {
      newIndex = index > 0 ? index - 1 : 0;
    } else if (index < localSectionIndex) {
      newIndex = localSectionIndex - 1;
    } else {
      newIndex = localSectionIndex;
    }
    const title = localSections[newIndex];
    setLocalSectionTitle(title);
    setLocalSectionIndex(newIndex);
  };

  const handleAddOption = () => {
    setLocalSectionOptions([
      ...localSectionOptions,
      {
        index: localSectionOptions.length,
        name: '',
        desc: '',
        sectionIndex: localSectionIndex,
      },
    ]);
  };

  const handleOptionNameChange = (index, value) => {
    localSectionOptions[index].name = value;
    setLocalSectionOptions([...localSectionOptions]);
  };

  const handleOptionDescChange = (index, value) => {
    localSectionOptions[index].desc = value;
    setLocalSectionOptions([...localSectionOptions]);
  };

  const handleOptionDelete = index => {
    const optionIndex = localOptions[index].index;
    setLocalSectionOptions(
      _cloneDeep(localSectionOptions)
        .filter(option => option.index !== optionIndex)
        .map((option, oIndex) => {
          const o = option;
          o.index = oIndex;
          return o;
        }),
    );
  };

  const handleOptionUp = index => {
    const optionIndexA = localOptions[index].index;
    const optionIndexB = localOptions[index - 1].index;
    const optionA = localSectionOptions[optionIndexA];
    const optionB = localSectionOptions[optionIndexB];
    const temp = { name: optionA.name, desc: optionA.desc };
    optionA.name = optionB.name;
    optionA.desc = optionB.desc;
    optionB.name = temp.name;
    optionB.desc = temp.desc;
    setLocalSectionOptions([...localSectionOptions]);
  };

  const handleOptionDown = index => {
    const optionIndexA = localOptions[index].index;
    const optionIndexB = localOptions[index + 1].index;
    const optionA = localSectionOptions[optionIndexA];
    const optionB = localSectionOptions[optionIndexB];
    const temp = { name: optionA.name, desc: optionA.desc };
    optionA.name = optionB.name;
    optionA.desc = optionB.desc;
    optionB.name = temp.name;
    optionB.desc = temp.desc;
    setLocalSectionOptions([...localSectionOptions]);
  };

  const buildData = () => {
    return {
      title: localTitle,
      subtitle: localSubtitle,
      desc: localDesc,
      listButtonText: localListButtonText,
      sections: localSections,
      buttons: _sortBy(
        localSectionOptions.map(option => ({
          type: 'postback',
          title: option.name,
          subtitle: option.desc,
          sectionIndex: option.sectionIndex,
        })),
        ['sectionIndex'],
      ),
    };
  };

  const isValid = validateWaLit(buildData());

  const waListCardContent = () => {
    switch (selectedTab.key) {
      case 'card':
        return (
          <div className={classes.root}>
            <div className={classes.contentCardContainer}>
              <div>
                <div
                  className={classes.templateTitle}
                  style={{ marginBottom: 12 }}
                >
                  {t('waList.title')}
                  <span className={classes.red}>*</span>
                  <span style={{ width: '20px', height: '20px' }}>
                    <Tooltip
                      light
                      placement="top"
                      title={t('waList.cardTitleTooitp')}
                    >
                      <img
                        style={{ width: '20px', height: '20px' }}
                        className={classes.marginLeft8}
                        src={icHelpCircle}
                        alt="alert"
                      />
                    </Tooltip>
                  </span>
                </div>
                <TextAreaCard
                  maxCount={60}
                  placeholder={t('waList.titlePlaceholder')}
                  value={localTitle}
                  onChange={v => {
                    setLocalTitle(v);
                    setIsModified(true);
                  }}
                  status={
                    (isModified && localTitle) === '' ||
                    /(\*|_|~|```)(.*?)\1/.test(localTitle)
                      ? 'error'
                      : undefined
                  }
                  disabled={disabled}
                />
              </div>
              <div>
                <div
                  className={classes.templateTitle}
                  style={{ margin: '12px 0px' }}
                >
                  {t('waList.subtitle')}
                  <span className={classes.red}>*</span>
                </div>
                <TextAreaCard
                  maxCount={1024}
                  placeholder={t('waList.subtitlePlaceholder')}
                  value={localSubtitle}
                  onChange={v => {
                    setLocalSubtitle(v);
                    setIsModified(true);
                  }}
                  status={
                    isModified && localSubtitle === '' ? 'error' : undefined
                  }
                  disabled={disabled}
                  textAreaHeight={100}
                />
              </div>
              <div>
                <div
                  className={classes.templateTitle}
                  style={{ margin: '12px 0px' }}
                >
                  {t('waList.desc')}
                </div>
                <TextAreaCard
                  maxCount={60}
                  placeholder={t('waList.descPlaceholder')}
                  value={localDesc}
                  onChange={v => {
                    setLocalDesc(v);
                    setIsModified(true);
                  }}
                  disabled={disabled}
                />
              </div>
              <div>
                <div
                  className={classes.templateTitle}
                  style={{ margin: '12px 0px' }}
                >
                  {t('waList.listButtonText')}
                  <span className={classes.red}>*</span>
                </div>
                <TextAreaCard
                  maxCount={20}
                  placeholder={t('waList.listButtonTextPlaceholder')}
                  value={localListButtonText}
                  onChange={v => {
                    setLocalListButtonText(v);
                    setIsModified(true);
                  }}
                  status={
                    isModified && localListButtonText === ''
                      ? 'error'
                      : undefined
                  }
                  disabled={disabled}
                />
              </div>
            </div>
          </div>
        );
      case 'list':
        return (
          <div className={classes.tabListRoot}>
            <div className={classes.tabListLeft}>
              <div className={classes.icon} onClick={handleAddSection}>
                <IconAddCircle id="icon-add-section" width={20} height={20} />
                {t('waList.addSection')}
              </div>
              <hr style={{ marginBottom: '16px' }} />
              {localSections.map((section, index) => (
                <ItemLabel
                  key={`wa-list-section-${index}`}
                  title={localSections?.[index] || ''}
                  subtitle={` (${getOptionCount(index)})`}
                  editTitle={t('waList.editSection')}
                  saveTitle={t('waList.saveSection')}
                  isEditable={isEditable}
                  isDeleteable={isDeleteable}
                  onClick={() => handleSectionClick(index)}
                  onEditClick={(e, v) => {
                    e.stopPropagation();
                    handleSectionEdit(index, v);
                  }}
                  onDeleteClick={e => {
                    e.stopPropagation();
                    handleSectionDelete(index);
                  }}
                />
              ))}
            </div>
            <div className={classes.tabListRight}>
              <div
                className={classes.tabListCount}
                style={{
                  color: isOverLimit ? color.$17 : undefined,
                }}
              >
                <div>
                  {t('waList.optionLimit')} : {optionLimit}
                </div>
                <div style={{ marginLeft: '12px' }}>
                  {t('waList.optionTotal')} : {localSectionOptions.length}
                </div>
              </div>
              <div className={classes.tabListTitle}>{localSectionTitle}</div>
              {localOptions.map((option, index) => (
                <WhatsAppListOption
                  key={`wa-list-option-${localSectionIndex}-${option.index}-${option.title}`}
                  index={index}
                  name={localOptions[index].name}
                  desc={localOptions[index].desc}
                  onNameChange={v => handleOptionNameChange(option.index, v)}
                  onDescChange={v => handleOptionDescChange(option.index, v)}
                  onDelete={() => handleOptionDelete(index)}
                  onUp={() => handleOptionUp(index)}
                  onDown={() => handleOptionDown(index)}
                  messageLength={localOptions.length}
                  disabled={disabled}
                />
              ))}
              <div
                className={classes.icon}
                style={{ margin: '8px 0' }}
                onClick={handleAddOption}
              >
                <IconAddCircle id="icon-add-option" width={20} height={20} />
                {t('waList.addOption')}
              </div>
              <Tip title={t('waList.tip')} text={t('waList.tipMessage')} />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  function handleSubmit() {
    onSubmit({
      name: templateName,
      contentType: 'application/x-template',
      data: {
        templateType: 'waList',
        elements: [buildData()],
      },
    });
  }

  async function handleSave() {
    setIsSaveLoading(true);
    await dispatch(
      saveDefaultTemplate(
        orgId,
        templateId,
        'waList',
        templateName,
        [buildData()],
        expiredAt,
      ),
    );
    setIsSaveLoading(false);
    onCancel();
    dispatch(
      setDefaultTemplate({
        isShow: true,
        templateType: 'waList',
        messageIndex,
      }),
    );
  }

  async function handleSaveAndUse() {
    setIsSaveLoading(true);
    await dispatch(
      saveDefaultTemplate(
        orgId,
        templateId,
        'waList',
        templateName,
        [buildData()],
        expiredAt,
      ),
    );
    setIsSaveLoading(false);
    onSubmit({
      name: templateName,
      contentType: 'application/x-template',
      data: {
        templateType: 'waList',
        elements: [buildData()],
      },
    });
    dispatch(
      setDefaultTemplate({
        isShow: false,
        templateType: 'waList',
        messageIndex,
      }),
    );
  }

  const contentComponent = (
    <>
      {!disableTemplate && !isMessageCenter && (
        <div className={classes.selectTemplateBlock}>
          {`${t('or')} `}
          <span
            className={classes.selectTemplate}
            onClick={() => {
              onCancel();
              dispatch(
                setDefaultTemplate({
                  isShow: true,
                  templateType: 'waList',
                  messageIndex,
                }),
              );
            }}
          >
            {t('select-by-default-template')}
          </span>
        </div>
      )}
      <Tabs
        className={classes.tabs}
        value={tabIndex}
        tabs={tabs}
        onChange={onTabIndexChange}
      />
      {waListCardContent()}
    </>
  );

  const actionComponent = (
    <div
      className={
        isSaveAsDefaultTemplate || isMessageCenter
          ? classes.actionBar
          : classes.actionBarNoSave
      }
    >
      {!disableTemplate && (
        <div className={classes.actionContainer}>
          <div style={{ display: 'flex' }}>
            {isMessageCenter ? (
              <span className={classes.templateTitle}>{t('templateName')}</span>
            ) : isShowMsgTemplate ? (
              <FormControlLabel
                className={classes.saveTemplateFormControlLabel}
                classes={{
                  label: classes.saveTemplateLabel,
                }}
                control={
                  <Checkbox
                    checked={isSaveAsDefaultTemplate}
                    onChange={() => {
                      dispatch(
                        setState({
                          isSaveAsDefaultTemplate: !isSaveAsDefaultTemplate,
                        }),
                      );
                    }}
                    name="checkedB"
                    color="primary"
                  />
                }
                label={t('save-as-default-template')}
              />
            ) : null}
            {(isSaveAsDefaultTemplate || isMessageCenter) && (
              <TextFieldCardCounter
                className={classes.saveTemplateName}
                maxCount={0}
                value={templateName}
                onChange={v => {
                  setTemplateName(v);
                }}
                status={!templateName ? 'error' : undefined}
                placeholder={t('key-in-template-name')}
              />
            )}
          </div>
        </div>
      )}
      <div className={classes.actionContainer} style={{ marginTop: 12 }}>
        {(isSaveAsDefaultTemplate || isMessageCenter) && (
          <div className={classes.expiredAtRow}>
            <FormControlLabel
              className={classnames(
                classes.saveTemplateFormControlLabel,
                classes.expiredAt,
              )}
              classes={{
                label: classes.saveTemplateLabel,
              }}
              control={
                <Checkbox
                  checked={!!expiredAt}
                  onChange={e => {
                    if (e.target.checked) {
                      setExpiredAt(
                        moment()
                          .add(config.DEFAULT_TEMPLATE_EXPIRED_DAY, 'days')
                          .hour(0)
                          .minute(0),
                      );
                    } else {
                      setExpiredAt(undefined);
                    }
                  }}
                  name="checkedB"
                  color="primary"
                />
              }
              label={
                <span className={classes.templateTitle}>
                  {t('save-template-expired')}
                  <Tooltip
                    light
                    placement="top"
                    title={t('save-template-expired-tooltip')}
                  >
                    <img
                      className={classes.marginLeft8}
                      src={icHelpCircle}
                      alt="alert"
                    />
                  </Tooltip>
                </span>
              }
            />
            {!!expiredAt && (
              <>
                <DatePicker
                  className={classes.date}
                  selectedDate={expiredAt}
                  minDate={moment()}
                  modalStyle={{
                    top: -430,
                  }}
                  onChange={date => {
                    setExpiredAt(
                      moment(
                        `${moment(date).format('YYYY-MM-DD')} ${moment(
                          expiredAt,
                        ).format('HH:mm')}`,
                      ),
                    );
                  }}
                  placeholder={t('datepicker.selectDate')}
                />
                <TimePicker
                  className={classes.time}
                  selectedTime={expiredAt}
                  isUp
                  onChange={value => {
                    setExpiredAt(
                      moment(
                        `${moment(expiredAt).format('YYYY-MM-DD')} ${moment(
                          value,
                        ).format('HH:mm')}`,
                      ),
                    );
                  }}
                  placeholder={t('datepicker.selectDate')}
                />
              </>
            )}
          </div>
        )}
        <div className={classes.opertationBlock}>
          <Button topic="primaryBlue" onClick={onCancel}>
            {t('modal.cancel')}
          </Button>
          {isSaveAsDefaultTemplate || isMessageCenter ? (
            <>
              <Button
                style={{ margin: '0 0 0 8px' }}
                topic="primaryBlue"
                onClick={handleSave}
                isDisable={!isValid || !templateName || templateName === ''}
                isLoading={isSaveLoading}
              >
                {t('modal.save')}
              </Button>
              {isShowSubmit && (
                <Button
                  style={{ margin: '0 0 0 8px' }}
                  topic="primaryBlueHighLight"
                  onClick={handleSaveAndUse}
                  isDisable={!isValid || !templateName || templateName === ''}
                  isLoading={isSaveLoading || isSendLoading}
                >
                  {isMessageCenter
                    ? t('modal.save-and-send')
                    : t('modal.save-and-submit')}
                </Button>
              )}
            </>
          ) : (
            <Button
              style={{ margin: '0 0 0 8px' }}
              topic="primaryBlue"
              onClick={handleSubmit}
              isDisable={!isValid}
            >
              {t('modal.sure')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
  return (
    <DialogText
      isOpen
      title={
        isMessageCenter
          ? t('template.create-tooltip')
          : t('templates.whatsapp-list-card')
      }
      content={contentComponent}
      contentClass={classes.contentContainer}
      actions={actionComponent}
    />
  );
}

WhatsAppListCardDialog.defaultProps = {
  onSubmit: () => {},
  onCancel: () => {},
  isSendLoading: false,
  isShowSubmit: true,
  isMessageCenter: false,
  data: {
    title: '',
    subtitle: '',
    buttons: [
      {
        type: 'postback',
        title: '',
        subtitle: '',
        sectionIndex: 0,
      },
    ],
    listButtonText: '',
    sections: [],
  },
};

WhatsAppListCardDialog.propTypes = {
  data: PropTypes.object,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  isMessageCenter: PropTypes.bool,
  isSendLoading: PropTypes.bool,
  isShowSubmit: PropTypes.bool,
};

export default WhatsAppListCardDialog;
